/**
 * "Current company" (2026-10 redesign): the app shows one company at a time, chosen in the header switcher.
 *
 *   const { current, companies, setCompany } = useCompany();
 *   current?.entity.id      → the company every page shows
 *   current?.row            → its row for the filing quarter (status, next action, balance) or null
 *   companies               → all companies in overview order, each with { entity, row, needsAction }
 *
 * The choice is remembered per account in localStorage. Without a (valid) remembered choice we pick the first
 * company in the overview order that needs the user to do something; if none does, simply the first one.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { journeyPosition, type EntityRefDTO, type EntityRowDTO, type JourneyPosition, type JourneyStep, type PeriodStatus } from '@btw/shared';
import { useEntities, useOverview } from '../api/hooks';
import { readStorage, writeStorage } from '../lib/hooks';
import type { MessageKey, TFunction } from '../i18n';
import type { PillTone } from '../components/kit';
import { useSession } from './session';

export interface Company {
  entity: EntityRefDTO;
  /** Row of the default filing quarter; null for a company without a return yet (e.g. just added). */
  row: EntityRowDTO | null;
  /** True when the next step is the user's (not waiting for the review, not closed). */
  needsAction: boolean;
}

export interface CompanyValue {
  companies: Company[];
  current: Company | null;
  setCompany: (entityId: string) => void;
  /** The quarter the rows belong to (default filing quarter from the overview), null while loading. */
  quarter: string | null;
  loading: boolean;
  error: unknown;
}

/** Statuses where nothing is expected from the user right now. */
const NO_ACTION: PeriodStatus[] = ['in_review', 'closed'];

export function needsAction(row: Pick<EntityRowDTO, 'status'> | null): boolean {
  return row === null || !NO_ACTION.includes(row.status);
}

/** Overview rows first (server order), then companies the overview does not list yet. */
export function buildCompanies(rows: EntityRowDTO[], entities: EntityRefDTO[]): Company[] {
  const seen = new Set<string>();
  const out: Company[] = [];
  for (const row of rows) {
    if (seen.has(row.entity.id)) continue;
    seen.add(row.entity.id);
    out.push({ entity: row.entity, row, needsAction: needsAction(row) });
  }
  for (const e of entities) {
    if (seen.has(e.id)) continue;
    seen.add(e.id);
    out.push({ entity: { id: e.id, name: e.name, legalForm: e.legalForm, color: e.color, initials: e.initials }, row: null, needsAction: true });
  }
  return out;
}

/** Remembered choice if it still exists; else the first company that needs action; else the first one. */
export function pickDefaultCompany(companies: Company[], storedId: string | null): string | null {
  if (storedId && companies.some((c) => c.entity.id === storedId)) return storedId;
  return (companies.find((c) => c.needsAction) ?? companies[0])?.entity.id ?? null;
}

export const companyStorageKey = (accountId: string) => `btw.company.${accountId}`;

/** Pill tone per journey step (prototype "Home"): step 1 sand, step 2 blue, step 3 peach, done green. */
const STEP_TONE: Record<JourneyStep, PillTone> = { step1: 'sand', step2: 'blue', step3: 'peach', done: 'green' };

/** Where this company is in the three-step journey (single mapping in @btw/shared: journeyPosition). */
export function companyJourney(c: Company): JourneyPosition | null {
  return c.row ? journeyPosition(c.row.status, { authorized: c.row.authorized }) : null;
}

/**
 * Pill tone + label for a company. Tone follows the journey step; the label names the step and what is left
 * ("Stap 2 · Nog 15 vragen", "Stap 3 · Invullen in het portaal"). Out of scope stays sand: it cannot be finished here.
 */
export function companyStatusPill(c: Company, t: TFunction): { tone: PillTone; label: string } {
  const r = c.row;
  if (!r) return { tone: 'sand', label: t('company.status.none') };
  // KOR (2026-10-30): no return; the pill shows how close the year's turnover is to the € 20,000 limit.
  if (r.kor) {
    if (r.kor.level === 'exceeded') return { tone: 'peach', label: t('company.status.kor.over') };
    return { tone: r.kor.level === 'ok' ? 'green' : r.kor.level === 'notice' ? 'blue' : 'peach', label: t('company.status.kor.ok', { n: r.kor.percent }) };
  }
  const pos = journeyPosition(r.status, { authorized: r.authorized });
  const tone = pos.blocked ? 'sand' : STEP_TONE[pos.step];
  if (pos.step === 'step3' && pos.sub === 'portal') return { tone, label: t('submit.status.portal') };
  const n = r.status === 'questions_open' ? r.openQuestions : r.status === 'review_differences' ? r.unresolvedDifferences : 0;
  return { tone, label: t(`company.status.${r.status}` as MessageKey, { n }) };
}

const CompanyContext = createContext<CompanyValue | null>(null);

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { account } = useSession();
  const accountId = account?.id ?? 'none';
  const overview = useOverview(null); // same query as QuarterProvider: one request
  const entities = useEntities();
  // Re-read storage when the account changes; `bump` re-renders after a choice.
  const [bump, setBump] = useState(0);
  const stored = useMemo(() => readStorage(companyStorageKey(accountId)), [accountId, bump]); // eslint-disable-line react-hooks/exhaustive-deps

  const companies = useMemo(() => buildCompanies(overview.data?.entities ?? [], entities.data ?? []), [overview.data, entities.data]);
  const currentId = pickDefaultCompany(companies, stored);

  const setCompany = useCallback(
    (id: string) => {
      writeStorage(companyStorageKey(accountId), id);
      setBump((b) => b + 1);
    },
    [accountId],
  );

  const value = useMemo<CompanyValue>(
    () => ({
      companies,
      current: companies.find((c) => c.entity.id === currentId) ?? null,
      setCompany,
      quarter: overview.data?.quarter ?? null,
      loading: overview.isLoading || entities.isLoading,
      error: overview.error ?? entities.error,
    }),
    [companies, currentId, setCompany, overview.data?.quarter, overview.isLoading, entities.isLoading, overview.error, entities.error],
  );
  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompany(): CompanyValue {
  const v = useContext(CompanyContext);
  if (!v) throw new Error('useCompany outside CompanyProvider');
  return v;
}
